export enum Type {
  ORGANIZATION_UNIT_SELECTION = 'ORGANIZATION_UNIT_SELECTION',
  ORGANIZATION_UNIT_COOPERATION_SELECTION = 'ORGANIZATION_UNIT_COOPERATION_SELECTION',
  DAY_SELECTION = 'DAY_SELECTION',
  STATIC_TEXT = 'STATIC_TEXT',
  SLIDER_GROUP = 'SLIDER_GROUP',
  SLIDER = 'SLIDER',
  SELECTION = 'SELECTION',
  DYNAMIC_TEXT = 'DYNAMIC_TEXT',
  SLIDER_DROPDOWN = 'SLIDER_DROPDOWN',
  SLIDER_DROPDOWN_GROUP = 'SLIDER_DROPDOWN_GROUP',
  BOOLEAN_SELECTION = 'BOOLEAN_SELECTION',
  NUMBER_INPUT = 'NUMBER_INPUT',
  DYNAMIC_NUMBER_DESCRIPTION_AMOUNT_INPUT = 'DYNAMIC_NUMBER_DESCRIPTION_AMOUNT_INPUT',
  AMOUNT_TOGGLE_INPUT = 'AMOUNT_TOGGLE_INPUT',
  ORGANIZATION_UNIT_NUMBER_INPUT = 'ORGANIZATION_UNIT_NUMBER_INPUT',
  DYNAMIC_OPTIONS_WITH_NUMBER_AND_SELECTION_OPTION = 'DYNAMIC_OPTIONS_WITH_NUMBER_AND_SELECTION_OPTION',
  DYNAMIC_OPTIONS_WITH_NUMBER_AND_SELECTION = 'DYNAMIC_OPTIONS_WITH_NUMBER_AND_SELECTION',
}

export enum PrebuildType {
  // TEAM
  ANWESENHEIT = 'ANWESENHEIT',
  ARBEITSTAG = 'ARBEITSTAG',
  EINZELARBEIT = 'EINZELARBEIT',
  MEETING_GROESSE_BESPRECHUNG = 'MEETING_GROESSE_BESPRECHUNG',
  MEETING_GROESSE_COWORKING = 'MEETING_GROESSE_COWORKING',
  TELEPHONE_MORE_THEN = 'TELEPHONE_MORE_THEN',
  // MANAGER
  STAUFLAECHE = 'STAUFLAECHE',
  SONDERBEDARFE = 'SONDERBEDARFE',
  WORKSTATIONS = 'WORKSTATIONS',
  SONDERBEDARFE_ZUSATZ = 'SONDERBEDARFE_ZUSATZ',
  RAEUMLICHE_VERORTUNG = 'RAEUMLICHE_VERORTUNG',
  RAEUMLICHE_VERORTUNG_NEIN = 'RAEUMLICHE_VERORTUNG_NEIN',
  // LEADER
  EINZELBUEROS = 'EINZELBUEROS',
  UEBERGEORDNETE_BEDARFE = 'UEBERGEORDNETE_BEDARFE',
}

export enum PrebuildArbeitstagSubType {
  EINZELARBEIT = 'EINZELARBEIT',
  KOMMUNIKATION = 'KOMMUNIKATION',
  KOLLABORATION = 'KOLLABORATION',
}

export enum PrebuildEinzelarbeitSubType {
  KLASSISCHE_ARBEIT = 'KLASSISCHE_ARBEIT',
  FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR = 'FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR',
  FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR = 'FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR',
}

export enum PrebuildMeetingGroesseBesprechungSubType {
  PERSON_2 = 'PERSON_2',
  PERSON_3_4 = 'PERSON_3_4',
  PERSON_5_8 = 'PERSON_5_8',
  PERSON_9_12 = 'PERSON_9_12',
  PERSON_13_16 = 'PERSON_13_16',
}

export enum PrebuildMeetingGroesseCoworkingSubType {
  PERSON_2_4 = 'PERSON_2_4',
  PERSON_5_8 = 'PERSON_5_8',
  PERSON_9_12 = 'PERSON_9_12',
  PERSON_13_16 = 'PERSON_13_16',
}

// core adds the basic properties to all elements

export type Core<T> = {
  uuid: string
  type: T
  prebuildType?:
    | PrebuildType
    | PrebuildArbeitstagSubType
    | PrebuildEinzelarbeitSubType
    | PrebuildMeetingGroesseBesprechungSubType
    | PrebuildMeetingGroesseCoworkingSubType
  title?: string
  helpText?: string
  description?: string
  infoImageKey?: string
  infoText?: string
}

// static text used for static text elements

export type StaticText = Core<Type.STATIC_TEXT>

// slider group used for multiple slider elements dependant on each other

export type SliderGroup = Core<Type.SLIDER_GROUP> & {
  sliders: Slider[]
}

// slider used for percentage questions

export type Slider = Core<Type.SLIDER> & {
  leftLabel?: string
  leftValue?: number
  rightLabel?: string
  rightValue?: number
  percentageLabel?: boolean
  enableCounterWeight?: boolean
  counterWeightText?: string
}

// selection used for single or multiple choice questions

export type SelectionOption = {
  uuid: string
  value: string
}

export type Selection = Core<Type.SELECTION> & {
  isMultipleChoice: boolean
  maxMultipleChoice: number
  forceMultipleChoiceMax: boolean
  options: SelectionOption[]
}

// dynamic text used for multiple single line text inputs

export type DynamicText = Core<Type.DYNAMIC_TEXT> & {
  maxAnswerCount: number
}

// day selection used for selecting a day

export type DaySelection = Core<Type.DAY_SELECTION>

// organization unit selection used for selecting an organization unit

export type OrganizationUnitSelection = Core<Type.ORGANIZATION_UNIT_SELECTION>

// organization unit selection used for selecting an organization unit

export type OrganizationUnitCooperationSelection = Core<Type.ORGANIZATION_UNIT_COOPERATION_SELECTION> & {
  maxMultipleChoice: number
}

// slider dropdown used for slider with dropdown options

export type SliderDropdownOption = {
  uuid: string
  value: string
}

export type SliderDropdown = Core<Type.SLIDER_DROPDOWN> &
  Omit<Slider, 'type'> & {
    dropdownOptions: SliderDropdownOption[]
  }

// slider dropdown group used for multiple slider elements dependant on each other with dropdowns

export type SliderDropdownGroup = Core<Type.SLIDER_DROPDOWN_GROUP> & {
  sliderDropdowns: SliderDropdown[]
}

// boolean selection used for yes/no questions

export type BooleanSelection = Core<Type.BOOLEAN_SELECTION> & {
  yesLabel: string
  noLabel: string
}

// number input used for number questions

export type NumberInput = Core<Type.NUMBER_INPUT> & {
  suffix?: string
}

// dynamic number description input used for number questions with dynamic description

export type DynamicNumberDescriptionAmountInput = Core<Type.DYNAMIC_NUMBER_DESCRIPTION_AMOUNT_INPUT> & {
  suffix?: string
  descriptionPlaceholder?: string
  amountSuffix?: string
  numberSuffix?: string
}

// dynamic number description input used for number questions with dynamic description

export type AmountToggleInput = Core<Type.AMOUNT_TOGGLE_INPUT> & {
  yesLabel?: string
  noLabel?: string
  amountSuffix?: string
}

// dynamic number description input used for number questions with dynamic description

export type OrganizationUnitNumberInput = Core<Type.ORGANIZATION_UNIT_NUMBER_INPUT>

// selectable options with number input

export type DynamicOptionsWithNumberAndSelectionOption = Core<Type.DYNAMIC_OPTIONS_WITH_NUMBER_AND_SELECTION_OPTION> & {
  uuid: string
  name: string
}

export type DynamicOptionsWithNumberAndSelection = Core<Type.DYNAMIC_OPTIONS_WITH_NUMBER_AND_SELECTION> & {
  numberSuffix: string
  normLabel: string
  dynamicOptionsWithNumberAndSelection?: DynamicOptionsWithNumberAndSelectionOption[]
}

export type Element =
  | SliderGroup
  | Slider
  | StaticText
  | Selection
  | DynamicText
  | DaySelection
  | OrganizationUnitSelection
  | OrganizationUnitCooperationSelection
  | SliderDropdown
  | SliderDropdownGroup
  | BooleanSelection
  | NumberInput
  | DynamicNumberDescriptionAmountInput
  | AmountToggleInput
  | OrganizationUnitNumberInput
  | DynamicOptionsWithNumberAndSelection
  | DynamicOptionsWithNumberAndSelectionOption
